// Dashboard page
import React, { useState, useEffect, useLayoutEffect, useCallback, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, ChevronDown, Star, Sparkles, Mic } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileDashboard } from '@/components/dashboard/MobileDashboard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { api } from '@/services/api';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { customToast } from '@/components/ui/custom-toast';
import { useHelpContext } from '@/components/help-widget/HelpContext';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { getCompanyInitials, getCompanyAvatarColor } from '@/lib/utils';
// Temporarily disabled to fix 429 rate limits - these cards make too many parallel API calls
// TODO: Re-enable after optimizing (batch endpoints, reduce polling frequency)
// import { CurrentlyProcessingCard } from '@/components/dashboard/CurrentlyProcessingCard';
// import { NeedsAttentionCard } from '@/components/dashboard/NeedsAttentionCard';
import { PickUpWhereYouLeftOffCard } from '@/components/dashboard/PickUpWhereYouLeftOffCard';
// PageBreadcrumb now rendered by AppLayout via BreadcrumbContext
import type { CompanyEpisodeStats } from '@/types/episode.types';

// Lazy load shell components via Module Federation
const AnnouncementBanner = React.lazy(() => import('shell/AnnouncementBanner'));
const ShellSpotlightCard = React.lazy(() => import('shell/SpotlightCard'));


interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  role: string;
  companyId: string;
  companyName: string; // Now always returned by /users/me API
  defaultLandingPage?: string; // User's preferred landing page path
}

// The path for Episode Composer dashboard in the parent app
const EPISODE_COMPOSER_PATH = '/tools/episode-composer/dashboard';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { openPanel, setMode, setContext } = useHelpContext();
  const { getCompanyColor } = useUserCompanies();
  const { setItems: setBreadcrumbItems } = useBreadcrumb();

  // Set breadcrumb items — runs before paint so bar persists across navigations
  useLayoutEffect(() => {
    setBreadcrumbItems([{ label: 'Episode Composer', icon: <Mic style={{ width: 14, height: 14, strokeWidth: 1.75 }} /> }]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  // Set help context for dashboard
  useEffect(() => {
    setContext('episode-composer:dashboard');
    return () => setContext(null);
  }, [setContext]);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [companyStats, setCompanyStats] = useState<CompanyEpisodeStats[]>([]);
  const [dataReady, setDataReady] = useState(false);
  const [creatingEpisode, setCreatingEpisode] = useState(false);
  const [isUpdatingPreference, setIsUpdatingPreference] = useState(false);

  // Spotlight state — fetched alongside initial data so it's ready at first paint
  const [dashboardSpotlight, setDashboardSpotlight] = useState<any | null>(null);

  // Check if Episode Composer is the default landing page
  const isDefaultLanding = user?.defaultLandingPage === EPISODE_COMPOSER_PATH;

  // Fetch user profile, company stats, and spotlights — batch all state updates
  useEffect(() => {
    const loadData = async () => {
      try {
        const [userResponse, stats] = await Promise.all([
          api.get<UserProfile>('/users/me'),
          PodcastEpisodeService.getMultiCompanyStats(),
        ]);

        let spotlight = null;

        if (userResponse.success && userResponse.data) {
          // Fetch spotlights before any setState calls
          try {
            const { SpotlightService } = await import('shell/SpotlightService');
            const spotlights = await SpotlightService.getUndismissedSpotlightsForContext(
              userResponse.data.id,
              'episode-composer:dashboard'
            );
            if (spotlights.length > 0) {
              spotlight = spotlights[0];
            }
          } catch (err) {
            console.error('Failed to load spotlights:', err);
          }

          // Single batch — all state set together so first render has everything
          setDashboardSpotlight(spotlight);
          setUser(userResponse.data);
        }
        setCompanyStats(stats);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setDataReady(true);
      }
    };

    loadData();
  }, []);

  // Determine if user has access to multiple companies
  const isMultiCompany = companyStats.length > 1;

  // Get user's first name for greeting
  const firstName = user?.displayName?.split(' ')[0] || 'there';

  // Dismiss a dashboard spotlight
  const handleDismissSpotlight = useCallback(async () => {
    if (!user || !dashboardSpotlight) return;
    try {
      const { SpotlightService } = await import('shell/SpotlightService');
      await SpotlightService.dismissSpotlight(user.id, dashboardSpotlight.id);
      setDashboardSpotlight(null);
    } catch (err) {
      console.error('Failed to dismiss spotlight:', err);
    }
  }, [user, dashboardSpotlight]);

  // Create episode handler - accepts optional companyId for multi-company users
  const handleCreateEpisode = useCallback(async (companyId?: string) => {
    try {
      setCreatingEpisode(true);
      const { id } = await PodcastEpisodeService.createDraftEpisode(undefined, companyId);
      customToast.success('Episode created');
      navigate(`../episodes/${id}`);
    } catch (err: any) {
      customToast.error('Failed to create episode', {
        description: err.message || 'Please try again',
      });
      setCreatingEpisode(false);
    }
  }, [navigate]);

  // Create episode for a specific company (multi-company flow)
  const handleCreateEpisodeForCompany = useCallback(async (companyId: string) => {
    handleCreateEpisode(companyId);
  }, [handleCreateEpisode]);

  // Handle toggling the default landing page preference
  const handleSetDefaultLanding = useCallback(async (checked: boolean) => {
    if (!user) return;

    setIsUpdatingPreference(true);
    try {
      // Use the /users/me/preferences endpoint which allows regular users to update their own preferences
      const response = await api.put('/users/me/preferences', {
        defaultLandingPage: checked ? EPISODE_COMPOSER_PATH : null,
      });

      if (response.success) {
        // Update local user state to reflect the change
        setUser((prev) => prev ? {
          ...prev,
          defaultLandingPage: checked ? EPISODE_COMPOSER_PATH : undefined,
        } : null);

        customToast.success(
          checked
            ? 'Episode Composer set as default landing page'
            : 'Default landing page cleared'
        );
      } else {
        customToast.error('Failed to update preference');
      }
    } catch (err) {
      console.error('Failed to update default landing page:', err);
      customToast.error('Failed to update preference');
    } finally {
      setIsUpdatingPreference(false);
    }
  }, [user]);

  if (isMobile) {
    return (
      <MobileDashboard
        user={user}
        companyStats={companyStats}
        dataReady={dataReady}
        isMultiCompany={isMultiCompany}
        isDefaultLanding={isDefaultLanding}
        dashboardSpotlight={dashboardSpotlight}
        onCreateEpisode={handleCreateEpisode}
        onCreateEpisodeForCompany={handleCreateEpisodeForCompany}
        onSetDefaultLanding={handleSetDefaultLanding}
        onDismissSpotlight={handleDismissSpotlight}
        creatingEpisode={creatingEpisode}
        isUpdatingPreference={isUpdatingPreference}
      />
    );
  }

  return (
    <>
      {/* Section 1: Welcome Header — contained card */}
      <div className="max-w-content" style={{ paddingLeft: '32px', paddingRight: '32px', paddingTop: '24px', paddingBottom: '24px' }}>
      <div className="bg-podcast rounded-lg border border-border shadow-sm" style={{ padding: '20px 24px 24px 24px' }}>
        <div>
          <div className="flex items-start justify-between">
            {/* Left side - Welcome message */}
            <div>
              <h1
                className="text-h1"
                style={{ color: 'var(--pm-text-primary)' }}
              >
                Welcome back{user && firstName !== 'there' && (
                  <span className="inline animate-fade-in">{`, ${firstName.charAt(0).toUpperCase()}${firstName.slice(1)}`}</span>
                )}
              </h1>
              <p className="mt-1" style={{ color: 'var(--pm-text-muted)' }}>
                Here's what's happening with your podcasts.
              </p>
            </div>

            {/* Right side - Create Episode Button + Default Landing Star */}
            {dataReady && (
            <div className="flex items-center gap-3 animate-fade-in">
              {/* Create Episode Button */}
              {isMultiCompany ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="create-episode-trigger"
                    className="flex items-center gap-2 rounded-lg font-medium transition-colors"
                    style={{
                      backgroundColor: creatingEpisode ? 'var(--pm-btn-primary-hover)' : 'var(--pm-btn-primary-bg)',
                      color: 'var(--pm-btn-primary-text)',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      fontWeight: 500,
                      opacity: creatingEpisode ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!creatingEpisode) e.currentTarget.style.backgroundColor = 'var(--pm-btn-primary-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!creatingEpisode) e.currentTarget.style.backgroundColor = 'var(--pm-btn-primary-bg)';
                    }}
                    disabled={creatingEpisode}
                  >
                    {creatingEpisode ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Create New Episode
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {companyStats.map((company) => {
                    const avatarColor = getCompanyColor(company.companyId) || getCompanyAvatarColor(company.companyId);
                    return (
                      <DropdownMenuItem
                        key={company.companyId}
                        data-testid={`company-option-${company.companyId}`}
                        data-company-id={company.companyId}
                        onClick={() => handleCreateEpisodeForCompany(company.companyId)}
                        className="flex items-center gap-3 py-2"
                      >
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarFallback
                            className="text-[10px] font-medium text-white"
                            style={{ backgroundColor: avatarColor }}
                          >
                            {getCompanyInitials(company.companyName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate">{company.companyName}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                className="flex items-center gap-2 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: creatingEpisode ? 'var(--pm-btn-primary-hover)' : 'var(--pm-btn-primary-bg)',
                  color: 'var(--pm-btn-primary-text)',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontWeight: 500,
                  opacity: creatingEpisode ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!creatingEpisode) e.currentTarget.style.backgroundColor = 'var(--pm-btn-primary-hover)';
                }}
                onMouseLeave={(e) => {
                  if (!creatingEpisode) e.currentTarget.style.backgroundColor = 'var(--pm-btn-primary-bg)';
                }}
                onClick={() => handleCreateEpisode()}
                disabled={creatingEpisode}
              >
                {creatingEpisode ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Create New Episode
              </button>
            )}

              {/* Set as default landing page star */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleSetDefaultLanding(!isDefaultLanding)}
                      disabled={isUpdatingPreference || !user}
                      className="p-2 rounded-lg transition-all hover:bg-white/10"
                      style={{
                        opacity: isUpdatingPreference ? 0.5 : 1,
                        cursor: isUpdatingPreference ? 'wait' : 'pointer',
                      }}
                      aria-label={isDefaultLanding ? 'Remove as default landing page' : 'Set as default landing page'}
                    >
                      <Star
                        className="w-5 h-5 transition-colors"
                        style={{
                          color: isDefaultLanding ? 'var(--pm-star-active)' : 'var(--pm-star-inactive)',
                          fill: isDefaultLanding ? 'var(--pm-star-active)' : 'transparent',
                        }}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{isDefaultLanding ? 'Remove as default landing page' : 'Set as default landing page'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Main Content - constrained width */}
      <div className="max-w-content pb-8" style={{ paddingLeft: '32px', paddingRight: '32px', paddingTop: '24px' }}>
        {/* Announcement Banner */}
        {user && (
          <Suspense fallback={null}>
            <AnnouncementBanner contextTrigger="episode-composer:dashboard" userId={user.id} />
          </Suspense>
        )}

        {/* Dashboard Cards - CurrentlyProcessingCard and NeedsAttentionCard temporarily disabled to fix 429 rate limits */}
        {/* TODO: Re-enable after optimizing API calls (batch endpoints, reduce polling) */}
        {/*
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <CurrentlyProcessingCard companyStats={companyStats} />
          <NeedsAttentionCard companyStats={companyStats} />
        </div>
        */}

        {/* Pick Up Where You Left Off + Spotlight/Help - 2 column layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {!dataReady ? (
            <>
              {/* Invisible placeholders — hold space without pulsing animation */}
              <div style={{ minHeight: '280px' }} />
              <div style={{ minHeight: '280px' }} />
            </>
          ) : (
          <>
          <div className="animate-fade-in">
            <PickUpWhereYouLeftOffCard companyStats={companyStats} />
          </div>

          {/* Spotlight (pre-fetched) or Help feature callout */}
          <div className="animate-fade-in">
          {dashboardSpotlight ? (
            <Suspense fallback={null}>
              <ShellSpotlightCard spotlight={dashboardSpotlight} onDismiss={handleDismissSpotlight} />
            </Suspense>
          ) : (
            <div
              className="rounded-xl flex flex-col items-center justify-center text-center border border-border"
              style={{
                backgroundColor: 'var(--pm-bg-card)',
                minHeight: '280px',
                padding: '32px 24px',
              }}
            >
              {/* Sparkle icons cluster */}
              <div className="relative mb-4" style={{ width: '48px', height: '48px' }}>
                <Sparkles
                  className="absolute"
                  style={{ color: 'var(--pm-btn-primary-bg)', width: '32px', height: '32px', top: '0', left: '8px' }}
                />
                <Sparkles
                  className="absolute"
                  style={{ color: 'var(--pm-btn-primary-bg)', width: '14px', height: '14px', top: '4px', left: '0', opacity: 0.7 }}
                />
                <Sparkles
                  className="absolute"
                  style={{ color: 'var(--pm-btn-primary-bg)', width: '10px', height: '10px', bottom: '8px', right: '0', opacity: 0.5 }}
                />
              </div>

              <h3
                className="font-semibold mb-2"
                style={{ color: 'var(--pm-text-primary)', fontSize: '18px' }}
              >
                New to Episode Composer?
              </h3>

              <p style={{ color: 'var(--pm-text-muted)', fontSize: '14px', maxWidth: '280px', marginBottom: '20px', lineHeight: '1.5' }}>
                Learn the basics and discover features to speed up your workflow.
              </p>

              <button
                onClick={() => {
                  setMode('browse');
                  openPanel();
                }}
                className="text-sm underline transition-colors"
                style={{ color: 'var(--pm-btn-primary-bg)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--pm-btn-primary-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--pm-btn-primary-bg)';
                }}
              >
                Browse all help articles
              </button>
            </div>
          )}
          </div>
          </>
          )}
        </div>
      </div>
    </>
  );
};
